import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { ErrorMessage, Validate, ValidateGroup } from '../../../../../components/Validation';
import Textbox from '../../../components/Textbox';
import OptionsContainer from '../../../components/OptionsContainer';
import PageSectionHeader from '../../../components/PageSectionHeader';
import messages from '../../messages';

/**
 * Renders a dynamic list of payment form fields based on configuration from the API.
 * Each field shape: { id, fieldName, fieldLabel, uiControl, tooltip, isCodeCheck, isEnableDefault, verificationErrorMessage, value, order, validateRule }
 * validateRule shape: { isMandatory, isOnlyDigits, maxLength, minLength }
 */
class DynamicPaymentFormFields extends React.Component {
  buildValidators(field) {
    const validators = [];
    const { validateRule } = field;
    if (!validateRule) return validators;

    if (validateRule.isMandatory) {
      const v = function (value) {
        if (value === undefined || value === null || value === '' || value === false) {
          return false;
        }
        return true;
      };
      v.messageKey = 'isRequired';
      validators.push(v);
    }

    if (validateRule.isOnlyDigits) {
      const v = function (value) {
        if (!value) return true; // empty is handled by isRequired
        return /^\d+$/.test(String(value));
      };
      v.messageKey = 'isOnlyDigits';
      validators.push(v);
    }

    if (validateRule.minLength) {
      const min = validateRule.minLength;
      const v = function (value) {
        if (!value) return true; // empty is handled by isRequired
        return String(value).length >= min;
      };
      v.messageKey = 'isMinLength';
      validators.push(v);
    }

    if (validateRule.maxLength) {
      const max = validateRule.maxLength;
      const v = function (value) {
        if (!value) return true;
        return String(value || '').length <= max;
      };
      v.messageKey = 'isMaxLength';
      validators.push(v);
    }

    return validators;
  }

  renderTextField(field, value, isReadOnly, validatorGroup, validationCounter) {
    const { onChange, onCodeFieldBlur, codeVerificationFailed } = this.props;
    const validators = this.buildValidators(field);
    const { validateRule } = field;

    // Trigger code lookup on blur/change only for:
    // 1. field.isCodeCheck is explicitly true, OR
    // 2. field is BankBranchCode (transit number) — backend may not set isCodeCheck=true
    const isLookupField = field.isCodeCheck === true || field.fieldName === 'BankBranchCode';
    const isExactLength = validateRule && validateRule.minLength && validateRule.maxLength && validateRule.minLength === validateRule.maxLength;

    const handleBlur = isLookupField && onCodeFieldBlur
      ? (e) => {
          const v = e.target.value;
          // Only trigger lookup when value meets the minimum required length
          if (v && (!validateRule || !validateRule.minLength || v.length >= validateRule.minLength)) {
            onCodeFieldBlur(field.fieldName, v, field.fieldLabel);
          }
        }
      : undefined;

    // Also trigger immediately when the value reaches the required fixed length (on change)
    const handleChange = (e) => {
      const v = e.target.value;
      onChange(field.fieldName, v);
      if (isLookupField && onCodeFieldBlur && validateRule && validateRule.minLength > 0 && v.length === validateRule.minLength) {
        onCodeFieldBlur(field.fieldName, v, field.fieldLabel);
      }
    };

    // Code-check fields (e.g. Routing Number) span the full width; other fields pair up two per row
    const columnClass = isLookupField ? 'col-md-12' : 'col-md-6';
    const verificationErrorLabel = field.verificationErrorMessage || `${field.fieldLabel} cannot be verified`;

    return (
      <div className={`${columnClass} form-group mb02`} key={field.id}>
        {validators.length > 0 ? (
          <Validate
            name={field.fieldName}
            group={validatorGroup}
            validators={validators}
            addValidatorToGroup={validatorGroup.addValidatorToGroup}
            validChangeInGroup={validatorGroup.validChangeInGroup}
            validationCounter={validationCounter}
            validatorLeaveGroup={validatorGroup.removeValidatorToGroup}
            fieldName={{ id: field.fieldName, defaultMessage: field.fieldLabel }}
            tooltipContent={field.tooltip || null}
          >
            <Textbox
              disabled={isReadOnly}
              name={field.fieldName}
              type={validateRule && validateRule.isOnlyDigits ? 'number' : 'text'}
              onChange={handleChange}
              onBlur={handleBlur}
              value={value || ''}
            />
            {validators.map((validator) => {
              let msgContent;
              switch (validator.messageKey) {
                case 'isRequired':
                  msgContent = <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} is required.</span>;
                  break;
                case 'isMinLength':
                  msgContent = isExactLength
                    ? <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} must be exactly {validateRule.minLength} digits.</span>
                    : <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} must contain at least {validateRule.minLength} digits.</span>;
                  break;
                case 'isMaxLength':
                  msgContent = isExactLength
                    ? <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} must be exactly {validateRule.maxLength} digits.</span>
                    : <span style={{ fontSize: '0.8em' }}>{field.fieldLabel} must contain no more than {validateRule.maxLength} digits.</span>;
                  break;
                case 'isOnlyDigits':
                  msgContent = <span style={{ fontSize: '0.8em' }}><FormattedMessage {...messages.isOnlyDigits} values={{ defaultMessage: field.fieldLabel }} /></span>;
                  break;
                default:
                  msgContent = <span style={{ fontSize: '0.8em' }}>{field.fieldLabel}</span>;
              }
              return (
                <ErrorMessage key={validator.messageKey} for={validator.messageKey}>
                  {msgContent}
                </ErrorMessage>
              );
            })}
          </Validate>
        ) : (
          <div>
            <PageSectionHeader>{field.fieldLabel}</PageSectionHeader>
            <Textbox
              disabled={isReadOnly}
              name={field.fieldName}
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              value={value || ''}
            />
          </div>
        )}
        {field.tooltip && (
          <p style={{ fontSize: '0.8em', color: '#555', marginTop: 2 }}>
            {field.tooltip}
          </p>
        )}
        {isLookupField && codeVerificationFailed && (
          <p style={{ fontSize: '0.7em', color: 'red', marginTop: 2 }}>
            {verificationErrorLabel}
          </p>
        )}
      </div>
    );
  }

  renderCheckboxField(field, value, isReadOnly) {
    const { onChange } = this.props;
    return (
      <div className={'col-md-12 form-group mb02'} key={field.id}>
        <OptionsContainer style={{ marginBottom: 0 }}>
          <input
            disabled={isReadOnly}
            type="checkbox"
            name={field.fieldName}
            checked={!!value}
            onChange={(e) => onChange(field.fieldName, e.target.checked)}
          />
          <span>{field.fieldLabel}</span>
        </OptionsContainer>
      </div>
    );
  }

  render() {
    const { fields, formValues, isReadOnly, validatorGroup, validationCounter } = this.props;

    if (!fields || fields.length === 0) {
      return null;
    }

    // 'Button' fields (e.g. a legacy manual "Validate" trigger) have no React rendering here - drop them.
    const sortedFields = [...fields]
      .filter((field) => (field.uiControl || '').toLowerCase() !== 'button')
      .sort((a, b) => a.order - b.order);
    const isInvalidBsbChecked = !!formValues['IsInvalidBsb'];

    // Share one Bootstrap row so col-md-6 fields sit two-per-line instead of one-per-line
    return (
      <div className="row">
        {sortedFields.map((field) => {
          const value = formValues[field.fieldName];
          // IsInvalidBsb checkbox: always interactive
          // Code-check fields (SWIFT/Transit/Routing): enabled when IsInvalidBsb is NOT checked
          // Fields flagged isEnableDefault by the backend are always editable
          // Remaining fields: enabled only when IsInvalidBsb IS checked
          const isCheckboxControl = field.fieldName === 'IsInvalidBsb';
          const isCodeCheckField = field.isCodeCheck === true || field.fieldName === 'BankBranchCode';
          let fieldDisabled;
          if (isCheckboxControl) {
            fieldDisabled = isReadOnly;
          } else if (isCodeCheckField) {
            fieldDisabled = isReadOnly || isInvalidBsbChecked;
          } else if (field.isEnableDefault) {
            fieldDisabled = isReadOnly;
          } else {
            fieldDisabled = isReadOnly || !isInvalidBsbChecked;
          }

          if (field.uiControl === 'checkbox') {
            return this.renderCheckboxField(field, value, fieldDisabled);
          }
          return this.renderTextField(field, value, fieldDisabled, validatorGroup, validationCounter);
        })}
      </div>
    );
  }
}

DynamicPaymentFormFields.propTypes = {
  fields: PropTypes.array,
  formValues: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onCodeFieldBlur: PropTypes.func,
  codeVerificationFailed: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  validatorGroup: PropTypes.object.isRequired,
  validationCounter: PropTypes.number,
};

DynamicPaymentFormFields.defaultProps = {
  fields: [],
  formValues: {},
  codeVerificationFailed: false,
  isReadOnly: false,
  validationCounter: 0,
};

export default DynamicPaymentFormFields;



// WEBPACK FOOTER //
// ./app/containers/FastCover/Appsure.StepBankDetails/components/DynamicPaymentFormFields/index.js